/**
 * ScheduleDateLabel.tsx
 *
 * The date in the Schedule toolbar, beside Prev / Today / Next (SCRUM-2136).
 *
 * The label takes whatever room the rest of the toolbar leaves. It was `flex-1 min-w-0 truncate`,
 * so it could be squeezed to any width: on a crowded toolbar it read "Monday, …", and with every
 * control on one line it read "Mo…" — the weekday, without the date.
 *
 * Invisible copies are laid out at their natural widths:
 *   - the widest short label the view can show (`samples`), plus room for the spinner, becomes the
 *     label's minimum, so the toolbar (which is `flex-wrap`) moves its other controls to the next
 *     line rather than squeezing the date below "Wed, 12/28";
 *   - the full form's width is compared with the room the label has, and the short form is shown
 *     when the full one would not fit.
 *
 * The minimum depends on the view and nothing else. One that followed the visible text would
 * change how the toolbar wraps, which changes the room, which changes the form — the two forms
 * would swap on every observation. One that followed the current date's short form (as this did
 * at first) re-wrapped the toolbar as the user paged from a narrow date to a wide one, moving the
 * view switcher between rows and the grid up and down under the cursor.
 *
 * The page's loading spinner is drawn here, after the date, rather than as a toolbar item beside
 * it. As a toolbar item it took ~24px from the label whenever a week loaded, which was enough to
 * swap the label to its short form for the length of the request. Here its room is reserved in
 * both the minimum and the choice of form, so it always fits and loading never changes the form.
 * It is the page's only loading cue: the grid is empty until a new week arrives.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { Spinner } from '@/components/ui/Spinner';
import { pickScheduleDateLabel, type ScheduleDateLabelText } from '@/pages/scheduleShared';

const MEASURE = 'invisible absolute left-0 top-0 whitespace-nowrap';

/** The `sm` spinner (h-4 w-4) and the gap before it. */
const SPINNER_ROOM = 16 + 8;

/**
 * Fractional width. `scrollWidth` / `clientWidth` round to whole pixels, and a minimum rounded
 * down by half a pixel is enough to put the ellipsis back on the short form.
 */
function widthOf(el: HTMLElement): number {
  return el.getBoundingClientRect().width;
}

interface Layout {
  form: 'full' | 'short';
  /** 0 until measured. */
  minWidth: number;
  /** The width of the text being shown, so the spinner can sit just after it. */
  textWidth: number;
  slotWidth: number;
}

const UNMEASURED: Layout = { form: 'full', minWidth: 0, textWidth: 0, slotWidth: 0 };

export function ScheduleDateLabel({ full, short, samples, busy = false }: ScheduleDateLabelText & { busy?: boolean }) {
  const slotRef = useRef<HTMLSpanElement>(null);
  const fullRef = useRef<HTMLSpanElement>(null);
  const shortRef = useRef<HTMLSpanElement>(null);
  const samplesRef = useRef<HTMLSpanElement>(null);
  const [layout, setLayout] = useState<Layout>(UNMEASURED);
  // The page builds a new array on every navigation; the samples only change with the view.
  const samplesKey = samples.join('\n');

  // Re-run when the text changes (navigating, switching view), not only on resize: the next
  // day's label can be wider than the one that fitted.
  useLayoutEffect(() => {
    const slot = slotRef.current;
    const fullCopy = fullRef.current;
    const shortCopy = shortRef.current;
    const sampleCopies = Array.from(samplesRef.current?.children ?? []) as HTMLElement[];
    if (!slot || !fullCopy || !shortCopy) return;
    const check = () => {
      const fullWidth = widthOf(fullCopy);
      const shortWidth = widthOf(shortCopy);
      const widest = Math.max(shortWidth, ...sampleCopies.map(widthOf));
      const minWidth = widest > 0 ? Math.ceil(widest) + SPINNER_ROOM : 0;
      // Apply the minimum before reading the room: it changes how the toolbar wraps, and so the
      // room. Reading first would choose the form for a layout that is about to change.
      if (minWidth > 0) slot.style.minWidth = `${minWidth}px`;
      const slotWidth = widthOf(slot);
      const form = pickScheduleDateLabel(fullWidth, slotWidth, SPINNER_ROOM);
      const next = { form, minWidth, textWidth: form === 'full' ? fullWidth : shortWidth, slotWidth };
      setLayout((prev) =>
        prev.form === next.form && prev.minWidth === next.minWidth
          && prev.textWidth === next.textWidth && prev.slotWidth === next.slotWidth
          ? prev
          : next,
      );
    };
    check();
    const observer = new ResizeObserver(check);
    // The copies too: a web font arriving after the first measurement widens them without
    // necessarily resizing the label.
    observer.observe(slot);
    observer.observe(fullCopy);
    observer.observe(shortCopy);
    sampleCopies.forEach((copy) => observer.observe(copy));
    return () => observer.disconnect();
  }, [full, short, samplesKey]);

  return (
    <span
      ref={slotRef}
      title={full}
      // 0 is "not measured yet", which leaves the label as it always was.
      style={layout.minWidth > 0 ? { minWidth: layout.minWidth } : undefined}
      // `tabular-nums`: every digit the same width, so a two-digit date is the widest `samples`
      // needs to cover.
      className="relative text-sm font-semibold text-slate-700 tabular-nums flex-1 min-w-0 truncate"
    >
      <span ref={fullRef} aria-hidden="true" className={MEASURE}>
        {full}
      </span>
      <span ref={shortRef} aria-hidden="true" className={MEASURE}>
        {short}
      </span>
      <span ref={samplesRef}>
        {samples.map((sample) => (
          <span key={sample} aria-hidden="true" className={MEASURE}>
            {sample}
          </span>
        ))}
      </span>
      {layout.form === 'short' ? short : full}
      {/* The minimum and the choice of form both keep SPINNER_ROOM free, so once measured it fits. */}
      {busy && layout.slotWidth > 0 && (
        <span className="absolute top-1/2 -translate-y-1/2 flex" style={{ left: layout.textWidth + 8 }}>
          <Spinner size="sm" />
        </span>
      )}
    </span>
  );
}
